import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createIntegrationConnectionsApi,
  PlaybookApiError,
  useApiClient,
  type IntegrationConnectionDetail,
} from "@playbookai/api-clients";

/**
 * PB-4734 (G9) — the connection an administrator's policy pickers discover
 * through, or the reason there isn't one.
 *
 * ## Why a picker needs a connection at all
 *
 * There is no tenant-wide credential to enumerate a provider with. Every
 * instance runs in `delegated_user` mode, so listing projects — or a
 * capability's issue types — happens over somebody's own authorized identity.
 * The administrator's own connection is the only one they may use: another
 * member's is not theirs to spend, and `requireOwnConnection` refuses it.
 *
 * ## Access is default-deny, and the CREATOR's grant is written, not implied
 *
 * Still default-deny: no grant row, nothing listed. What changed is that
 * creating an instance now seeds the creator's own grant in the same
 * transaction as the create, so the administrator who made it does not have to
 * grant themselves access on another screen first. The order is connect your
 * own account, then the pickers populate. Everyone else needs a grant somebody
 * gave them, exactly as before.
 *
 * ## Distinguishable causes, never an empty dropdown
 *
 * An empty list reads as "this tenant has no projects", which is a different
 * and much more alarming statement than "you have not connected yet". So this
 * returns WHY, and the caller renders the matching next step.
 */
export type PolicyDiscoveryState =
  | { readonly kind: "ready"; readonly connectionId: string }
  | { readonly kind: "loading" }
  /** No connection on this instance at all — the usual first-run case. */
  | { readonly kind: "not-connected" }
  /** Connected once, but the credential has lapsed or been disconnected. */
  | { readonly kind: "connection-unusable"; readonly status: string }
  /**
   * The connections read itself FAILED, so we do not know what exists.
   *
   * Distinct from `not-connected` on purpose. Treating a failed request as
   * "you have not connected" states a fact we did not establish, and sends the
   * administrator to set up a connection they may already have.
   */
  | { readonly kind: "unavailable" };

export function usePolicyDiscovery(
  tenantIntegrationId: string | undefined,
): PolicyDiscoveryState {
  const config = useApiClient();
  const api = useMemo(() => createIntegrationConnectionsApi(config), [config]);

  const connections = useQuery({
    queryKey: ["integration-connections", "for-policy"],
    queryFn: () => api.listMine(),
    enabled: tenantIntegrationId !== undefined,
  });

  if (tenantIntegrationId === undefined || connections.isPending) {
    return { kind: "loading" };
  }
  if (connections.isError) return { kind: "unavailable" };

  const mine = (connections.data ?? []).filter(
    (c: IntegrationConnectionDetail) =>
      c.tenantIntegrationId === tenantIntegrationId,
  );
  if (mine.length === 0) return { kind: "not-connected" };

  const active = mine.find((c) => c.status === "active");
  if (active !== undefined) {
    return { kind: "ready", connectionId: active.id };
  }
  // Report the FIRST one's status rather than a generic "unusable": expired and
  // revoked need different actions, and the row that says which is on another
  // page.
  return { kind: "connection-unusable", status: mine[0]!.status };
}

/**
 * The one thing NOT distinguished here: a missing grant.
 *
 * `listResources` answers `INTEGRATION_NOT_GRANTED` for an administrator who
 * has not granted themselves access, and that is a server-side fact this hook
 * cannot see — connections list fine without a grant. So the third cause is
 * detected from the FAILURE of the discovery call, not from this state, and the
 * caller passes the error through `discoveryBlockedReason`.
 */
export function discoveryBlockedReason(error: unknown): string | null {
  // Reads the TYPED envelope, not the message text. `PlaybookApiError` carries
  // `envelope.code` precisely so a caller never has to substring-match prose:
  // the message is a human sentence the server may reword at any time, and a
  // match against it would silently stop recognising the case — leaving a
  // blocked administrator with the generic "provider did not answer" instead of
  // the one instruction that unblocks them.
  const code =
    error instanceof PlaybookApiError ? error.envelope.code : undefined;
  if (code === "INTEGRATION_NOT_GRANTED") {
    return "You have not granted yourself access to this integration yet. Add yourself under Access, then reload.";
  }
  if (code === "INTEGRATION_CONNECTION_UNUSABLE") {
    return "Your connection to this system is no longer usable. Reconnect it under Your connections.";
  }
  return null;
}
